import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, HStack, SystemStyleObject, Text } from "@chakra-ui/react";
import {
  FormatOptionLabelMeta,
  GroupBase,
  MenuPlacement,
  MultiValue,
  MultiValueProps,
  PropsValue,
  Select,
  SingleValue,
  SingleValueProps,
} from "chakra-react-select";
import { useUsers } from "modules/providers/users-provider";
import colors from "app/colors";
import { useTags } from "modules/providers/tags-provider/tags-provider";
import { useDistributionGroups } from "modules/providers/distribution-groups-provider";
import TagOptionLabel from "./tag-option-label";
import UserOptionLabel from "./user-option-label";
import DistributionGroupOptionLabel from "./distribution-group-option-label";
import { CirclePlusIcon } from "modules/icons";
import { TagFragment, UserRoleType } from "gql/graphql";
import EmailUserOptionLabel from "./email-user-option-label";
import FloatingLabel from "../_shared/floating-label";
import SelectedUserOption from "./selected-user-option";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import { CALLOUT_TYPE, CALLOUT_KEY } from "modules/utils/Constants";
import { SELECT_MENU_PORTAL_Z_INDEX } from "modules/providers/chakra-provider/chakra-portal-stack";
import {
  sortUsersByCallout,
  sortUsersByEmailAddress,
  sortTags,
  sortByName,
  isMultiValue,
  getUserFullName,
  getTagMention,
  getDistributionGroupMention,
} from "modules/utils/Functions";

interface IProps {
  onSelectedOptionsChanged: (options: Option[]) => void;
  types: CALLOUT_TYPE[];
  menuPlacement?: MenuPlacement;
  defaultValues?: Option[];
  value?: Option[]; //allows controlled usage
  isMultiSelect: boolean;
  placeHolder?: string;
  includeEmailUsers?: boolean;
  floatingPlaceholder?: boolean;
  allowInactive?: boolean; //allows inactive users, tags, and distribution groups to be selected
  allowTagCreation?: boolean; //admin only; types must also include Tag
  displayAddAllUsers?: boolean; //admin only; types must also include User
  excludedIds?: string[]; // IDs to exclude from selection
  firstIds?: string[]; //IDs to display first
}

export interface Option {
  id: string;
  type: CALLOUT_TYPE;
  active: boolean;
}

interface Group extends GroupBase<Option> {
  label?: string;
  options: Option[];
}

const UserTagGroupSelector = ({
  onSelectedOptionsChanged,
  types,
  menuPlacement,
  defaultValues,
  isMultiSelect,
  placeHolder,
  value,
  includeEmailUsers = false,
  floatingPlaceholder = false,
  allowInactive = true,
  allowTagCreation = false,
  displayAddAllUsers = false,
  excludedIds = [],
  firstIds = [],
}: IProps) => {
  const { openModal } = useOverlay();
  const { nonEmailActiveUsers, nonEmailUsers, usersById, exposedEmailUsers } =
    useUsers();
  const { activeTags, tags, tagsById } = useTags();
  const {
    activeDistributionGroups,
    distributionGroups,
    distributionGroupsById,
  } = useDistributionGroups();
  const [search, setSearch] = useState("");
  const [_value, setValue] = useState<PropsValue<Option>>();

  //If the value is set to undefined, we need to explicitly set it to null
  useEffect(() => {
    if (!value) {
      setValue(null);
      return;
    }
    setValue(value);
  }, [value]);

  const createNewTagButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (defaultValues) {
      onChange(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const data = useMemo(() => {
    const data: Option[] = [];
    if (types.includes("user")) {
      data.push(
        ...(allowInactive ? nonEmailUsers : nonEmailActiveUsers)
          .sort(sortUsersByCallout)
          .filter((user) => !excludedIds.includes(user.id))
          .map(
            (user): Option => ({
              id: user.id,
              active: user.active,
              type: "user",
            }),
          ),
      );
    }
    if (includeEmailUsers) {
      data.push(
        ...exposedEmailUsers
          .filter((user) => !excludedIds.includes(user.id))
          .sort(sortUsersByEmailAddress)
          .map(
            (user): Option => ({
              id: user.id,
              active: user.active,
              type: "user",
            }),
          ),
      );
    }
    if (types.includes("tag")) {
      data.push(
        ...(allowInactive ? tags : activeTags)
          .filter((tag) => !excludedIds.includes(tag.id))
          .sort(sortTags)
          .map(
            (tag): Option => ({ id: tag.id, active: tag.active, type: "tag" }),
          ),
      );
    }
    if (types.includes("distribution-group")) {
      data.push(
        ...(allowInactive
          ? [...distributionGroups]
          : [...activeDistributionGroups]
        )
          .filter(
            (distributionGroup) => !excludedIds.includes(distributionGroup.id),
          )
          .sort(sortByName)
          .map(
            (distributionGroup): Option => ({
              id: distributionGroup.id,
              active: distributionGroup.active,
              type: "distribution-group",
            }),
          ),
      );
    }
    if (firstIds.length > 0) {
      // Move items with ids in firstIds to the front
      data.sort((a, b) => {
        const aIndex = firstIds.indexOf(a.id);
        const bIndex = firstIds.indexOf(b.id);
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
    }
    return data;
  }, [
    types,
    includeEmailUsers,
    firstIds,
    allowInactive,
    nonEmailUsers,
    nonEmailActiveUsers,
    excludedIds,
    exposedEmailUsers,
    tags,
    activeTags,
    distributionGroups,
    activeDistributionGroups,
  ]);

  const groups = useMemo(
    (): Group[] => [
      {
        options: data.filter((option) => option.active),
      },
      {
        label: "DEACTIVATED",
        options: data.filter((option) => !option.active),
      },
    ],
    [data],
  );

  const onChange = useCallback(
    (options: SingleValue<Option> | MultiValue<Option>) => {
      setValue(options);
      if (options === null) {
        onSelectedOptionsChanged([]);
        return;
      }
      if (isMultiValue(options)) {
        onSelectedOptionsChanged([...options]);
      } else {
        onSelectedOptionsChanged([
          {
            ...options,
          },
        ]);
      }
    },
    [onSelectedOptionsChanged],
  );

  const onInputChanged = useCallback((newValue: string) => {
    setSearch(newValue);
  }, []);

  const formatOptionLabel = useCallback(
    (option: Option, { context }: FormatOptionLabelMeta<Option>) => {
      switch (option.type) {
        case "user": {
          const user = usersById[option.id];
          if (!user) return null;
          if (user.roleType === UserRoleType.Email) {
            return (
              <EmailUserOptionLabel
                user={usersById[option.id]}
                search={search}
              />
            );
          }
          if (context === "value") {
            return <SelectedUserOption user={user} search={search} />;
          }
          return <UserOptionLabel user={user} search={search} />;
        }
        case "tag": {
          return <TagOptionLabel tag={tagsById[option.id]} search={search} />;
        }
        case "distribution-group": {
          return (
            <DistributionGroupOptionLabel
              distributionGroup={distributionGroupsById[option.id]}
              search={search}
            />
          );
        }
        default:
          return <Text>not found</Text>;
      }
    },
    [distributionGroupsById, search, tagsById, usersById],
  );

  const getOptionLabel = useCallback(
    (option: Option) => {
      switch (option.type) {
        case "user": {
          const user = usersById[option.id];
          if (user) {
            return `${CALLOUT_KEY}${user.callout} ${getUserFullName(user)}`;
          }
          return "";
        }
        case "tag": {
          const tag = tagsById[option.id];
          if (tag) {
            return `${getTagMention(tag)}`;
          }
          return "";
        }
        case "distribution-group": {
          const distributionGroup = distributionGroupsById[option.id];
          if (distributionGroup) {
            return getDistributionGroupMention(distributionGroup);
          }
          return "";
        }
        default:
          return "";
      }
    },
    [distributionGroupsById, tagsById, usersById],
  );

  const getOptionValue = useCallback((option: Option) => option.id, []);

  const isOptionArchived = useCallback(
    (
      state:
        | MultiValueProps<Option, boolean, GroupBase<Option>>
        | SingleValueProps<Option, boolean, GroupBase<Option>>,
    ) => !state.data.active,
    [],
  );

  const onTagsCreated = useCallback(
    (tags: TagFragment[]) => {
      const options: Option[] = tags.map((tag) => ({
        id: tag.id,
        type: "tag",
        active: tag.active,
      }));
      setValue((previous) => {
        if (isMultiValue(previous)) {
          onChange([...previous, ...options]);
          return [...previous, ...options];
        }
        if (options.length === 1) {
          onChange(options[0]);
        }
        return options[0];
      });
    },
    [onChange],
  );

  const onCreateNewTagClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.ADMIN_CREATE_TAG,
      onSaveCallback: onTagsCreated,
    });
  }, [onTagsCreated, openModal]);

  const onAddAllUsersClicked = useCallback(() => {
    setValue(
      nonEmailActiveUsers
        .filter((obj) => obj.active)
        .map((obj) => ({ id: obj.id, type: "user", active: obj.active })),
    );
    onChange(
      nonEmailActiveUsers
        .filter((obj) => obj.active)
        .map((obj) => ({ id: obj.id, type: "user", active: obj.active })),
    );
  }, [nonEmailActiveUsers, onChange]);

  return (
    <>
      {displayAddAllUsers && types.includes("user") && (
        <HStack w="full">
          <Button
            height={5}
            p={0}
            variant="ghost"
            onClick={onAddAllUsersClicked}
            color={colors.blue}
          >
            <HStack spacing={1}>
              <CirclePlusIcon />
              <Text>Add All Users</Text>
            </HStack>
          </Button>
        </HStack>
      )}
      <FloatingLabel label={floatingPlaceholder ? placeHolder : undefined}>
        <Select
          formatOptionLabel={formatOptionLabel}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          menuPlacement={menuPlacement || "bottom"}
          options={groups}
          onChange={onChange}
          placeholder={floatingPlaceholder ? "" : placeHolder}
          value={value ?? _value}
          chakraStyles={{
            placeholder: (provided: SystemStyleObject): SystemStyleObject => ({
              ...provided,
              fontStyle: "italic",
              color: colors.placeholderText,
            }),
            multiValue: (provided: SystemStyleObject): SystemStyleObject => ({
              ...provided,
              borderRadius: "60px",
              borderWidth: "1px",
              borderColor: colors.blue,
              backgroundColor: colors.white,
            }),
            groupHeading: (
              provided: SystemStyleObject,
              state,
            ): SystemStyleObject => {
              const currentGroup = state.data as GroupBase<Option>;
              return {
                ...provided,
                fontSize: 14,
                fontWeight: 500,
                color: colors.grayMain,
                paddingTop: 3,
                display: currentGroup.label ? "block" : "none",
              };
            },
            singleValue: (
              provided: SystemStyleObject,
              state:
                | MultiValueProps<Option, boolean, GroupBase<Option>>
                | SingleValueProps<Option, boolean, GroupBase<Option>>,
            ): SystemStyleObject =>
              state.isMulti
                ? {
                    ...provided,
                    borderRadius: "60px",
                    height: "24px",
                    backgroundColor: isOptionArchived(state)
                      ? colors.gray0
                      : colors.blueLight,
                  }
                : {
                    ...provided,
                    height: "24px",
                    backgroundColor: isOptionArchived(state)
                      ? colors.gray0
                      : colors.white,
                  },
            container: (provided: SystemStyleObject): SystemStyleObject => ({
              ...provided,
              width: "full",
              borderColor: colors.grayPrimary,
            }),
            menu: (provided: SystemStyleObject): SystemStyleObject => ({
              ...provided,
              boxShadow: "lg",
            }),
            multiValueRemove: (
              provided: SystemStyleObject,
            ): SystemStyleObject => ({
              ...provided,
              color: colors.blue,
            }),
          }}
          isMulti={isMultiSelect}
          components={{
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null,
          }}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({
              ...base,
              zIndex: SELECT_MENU_PORTAL_Z_INDEX,
            }),
          }}
          onInputChange={onInputChanged}
          inputValue={search}
        />
      </FloatingLabel>

      {allowTagCreation && types.includes("tag") && (
        <Button
          ref={createNewTagButtonRef}
          height={5}
          p={0}
          variant="ghost"
          onClick={onCreateNewTagClicked}
        >
          <HStack spacing={1} color={colors.blue}>
            <CirclePlusIcon fill={colors.blue} />
            <Text>Create new Tag</Text>
          </HStack>
        </Button>
      )}
    </>
  );
};

export default memo(UserTagGroupSelector);
